import { Row, Table } from '@tanstack/react-table'
import { ComponentType, Fragment, forwardRef, ForwardedRef } from 'react'
import { twMerge } from 'tailwind-merge'
import { Spinner } from '../Spinner/Spinner'
import { BaseTableDisplay, BaseTableHeader, BaseTableRow, BaseTableHeaderCell, BaseTableBody, BaseTableDataCell } from '../Table/Primitives'
import { NomTableContext } from './Context'
import { EmptyView, TableFilters, TablePagination } from './NomTablePlugins'

export interface TableDisplayProps<D> {
  table: Table<D>
  className?: string | undefined
  components?: {
    RowDetails?: ComponentType<{ row: Row<D> }> | undefined
  }
}
export const NomTableDisplay = forwardRef(
  function NomTableDisplay <D,> (props: TableDisplayProps<D>, ref: ForwardedRef<HTMLTableElement>) {
    const { RowDetails } = props.components ?? {}

    return (
      <BaseTableDisplay
        className={twMerge(
          'grid w-full auto-rows-[minmax(12,max-content)] grid-rows-[8] place-content-start place-self-start overflow-x-auto',
          props.className
        )}
        ref={ref}
        style={{
          gridTemplateColumns: props.table.getVisibleLeafColumns()
            .map(column => column.columnDef)
            .map(column =>
              column.meta?.gridWidth ??
              `minmax(${column.meta?.gridMinWidth ?? 'min-content'}, ${column.meta?.gridMaxWidth ?? '1fr'})`
            )
            .join(' ')
        }}
      >
        <BaseTableHeader>
          {props.table.getHeaderGroups().map(headerGroup => (
            <BaseTableRow key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <BaseTableHeaderCell
                  key={header.id}
                  onClick={() => header.column.getCanSort() && header.column.toggleSorting()}
                  className={twMerge(
                    header.column.getCanSort() && 'cursor-pointer',
                    header.column.getIsSorted() === 'desc' && "after:ml-4 after:content-['↓']",
                    header.column.getIsSorted() === 'asc' && "after:ml-4 after:content-['↑']"
                  )}
                >
                  {typeof header.column.columnDef.header === 'string'
                    ? header.column.columnDef.header
                    : header.column.columnDef.header?.(header.getContext())}
                </BaseTableHeaderCell>
              ))}
            </BaseTableRow>
          ))}
        </BaseTableHeader>

        <BaseTableBody>
          {props.table.getRowModel().rows.map(row => (
            <Fragment key={row.id}>
              <BaseTableRow
                className={twMerge(row.getIsSelected() && 'core-highlighted')}
              >
                {row.getVisibleCells().map(cell => (
                  <BaseTableDataCell key={cell.id}>
                    {typeof cell.column.columnDef.cell === 'string'
                      ? cell.column.columnDef.cell
                      : cell.column.columnDef.cell?.(cell.getContext())}
                  </BaseTableDataCell>
                ))}
              </BaseTableRow>

              {row.getIsExpanded() && RowDetails && (
                <tr className='contents'>
                  <td className='contents'>
                    <RowDetails row={row} />
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </BaseTableBody>

      </BaseTableDisplay>
    )
  }
)

export interface TableProps<D> {
  table: Table<D>
  className?: string | undefined
  loading?: boolean
  components?: {
    RowDetails?: ComponentType<{ row: Row<D> }>
    Pagination?: ComponentType
    Search?: ComponentType
    Filters?: ComponentType
    NoDataView?: ComponentType
  }
}
export const NomTable = forwardRef(
  function NomTable <D,> (props: TableProps<D>, ref: ForwardedRef<HTMLDivElement>) {
    const components = props.components ?? {}
    const {
      RowDetails,
      Pagination = TablePagination,
      Search,
      Filters = TableFilters,
      NoDataView
    } = components

    return (
      <NomTableContext.Provider
        value={{
          tableInstance: { ...props.table as any }
        }}
      >
        <div
          ref={ref}
          className={twMerge('grid', props.className)}
          style={{
            gridTemplateAreas: '"search filters empty loading pagination extras" "display display display display display display"',
            gridTemplateColumns: 'auto auto 1fr auto auto auto'
          }}
        >
          {(Search != null) && <Search />}

          <Filters />

          <Pagination />

          <Spinner
            className={twMerge(
              'mx-2 place-self-center [grid-area:loading]',
              (props.loading !== true) && 'opacity-0'
            )}
          />

          {(NoDataView != null && props.loading !== true) && <EmptyView><NoDataView /></EmptyView>}

          <NomTableDisplay
            table={props.table}
            className='[grid-area:display]'
            components={{ RowDetails }}
          />
        </div>
      </NomTableContext.Provider>
    )
  }
)
